import type { Nullable } from 'corgi-types';
import type { CategoryOptionFilter } from 'graphql-ts-types';

import {
  createOptionsParamValue,
  createRangeMaxParamName,
  createRangeMinParamName,
} from 'common/components/ClearAllFiltersButton';
import type { RangeDisplayProps } from 'common/components/RangeDisplay';
import type { Filter } from 'common/types/SortAndFilter';
import { getCurrentUrl, getUrlWithoutQueryParams, getUrlWithQueryParams } from 'common/utils/url';

const RANGE_FILTERS_WITH_LABEL = ['width', 'height', 'depth', 'diameter'];

export interface SelectedFilter {
  key: string;
  url: string;
  name: string;
  text: Nullable<string>;
  range?: RangeDisplayProps;
  filterKey?: string;
}

const getUrlWithoutFilterOption = (filter: CategoryOptionFilter, optionValue: string) => {
  const visibleOptionsKeys = filter.options
    .filter(option => option.selected && option.value !== optionValue)
    .map(option => option.key);

  const currentUrl = getCurrentUrl();
  let hiddenValues: string[] = [];

  if (currentUrl) {
    try {
      const urlObj = new URL(currentUrl);
      const currentFilterValue = urlObj.searchParams.get(filter.key);

      if (currentFilterValue) {
        const currentValues = currentFilterValue.split('|');
        const knownKeys = new Set(filter.options.map(opt => opt.key));
        hiddenValues = currentValues.filter(val => !knownKeys.has(val));
      }
    } catch {
      // This ensures the function remains functional even with malformed URLs
    }
  }

  const allOptionsKeys = [...visibleOptionsKeys, ...hiddenValues];
  const queryParamValue = createOptionsParamValue(allOptionsKeys);

  return getUrlWithQueryParams(
    {
      [filter.key]: queryParamValue,
    },
    getUrlWithoutQueryParams([filter.key])
  );
};

const getRangeText = ({ name, key }: Filter) =>
  RANGE_FILTERS_WITH_LABEL.includes(key) ? name : null;

export const getAllSelectedFilters = (filters: Filter[]): SelectedFilter[] =>
  [].concat(
    ...filters.map(filter => {
      if ('options' in filter) {
        return filter.options
          .filter(option => option.selected)
          .map(option => ({
            key: option.value,
            text: option.value,
            url: getUrlWithoutFilterOption(filter, option.value),
            name: filter.name,
            filterKey: filter.key,
          }));
      }
      if (filter.selectedMin || filter.selectedMax) {
        return {
          key: filter.name,
          range: filter,
          name: filter.name,
          url: getUrlWithoutQueryParams([
            createRangeMinParamName(filter.key),
            createRangeMaxParamName(filter.key),
          ]),
          text: getRangeText(filter),
          filterKey: filter.key,
        };
      }

      return [];
    })
  );
